import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, User, ChevronRight, RefreshCw, AlertCircle, Quote, Heart, BarChart3, CheckCircle2, Sparkles, Activity, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { formatDistanceToNow } from 'date-fns';

interface SocialInboxProps {
  brandId: string;
  accounts: any[];
  publishedPosts: any[]; // These are the posts already published through the platform
  onReadPost?: () => void;
}

const safeDateFormat = (timestampStr: string | undefined, fallback: string = 'recent') => {
  if (!timestampStr) return fallback;
  try {
    const date = new Date(timestampStr);
    if (isNaN(date.getTime())) return fallback;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (err) {
    return fallback;
  }
};

export const SocialInbox: React.FC<SocialInboxProps> = ({ brandId, accounts, publishedPosts, onReadPost }) => {
  const [allPosts, setAllPosts] = useState<any[]>([]);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [readPostIds, setReadPostIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(`readPostIds_${brandId}`) || '[]');
    } catch {
      return [];
    }
  });
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingInbox, setFetchingInbox] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyTexts, setReplyTexts] = useState<Record<string, string>>({});
  const [isReplying, setIsReplying] = useState<Record<string, boolean>>({});
  const [activeDetailTab, setActiveDetailTab] = useState<'comments' | 'likes'>('comments');
  const [limit, setLimit] = useState(10);
  const [sentimentMap, setSentimentMap] = useState<Record<string, any>>({});
  const [isAnalyzing, setIsAnalyzing] = useState<Record<string, boolean>>({});
  const [warnings, setWarnings] = useState<Record<string, string>>({});

  const analyzeSentiment = async (commentId: string, text: string) => {
    if (!text?.trim() || isAnalyzing[commentId]) return;
    setIsAnalyzing(prev => ({ ...prev, [commentId]: true }));
    try {
      const response = await fetch('/api/social/sentiment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });
      const data = await response.json();
      if (data.analysis) {
        setSentimentMap(prev => ({ ...prev, [commentId]: data.analysis }));
        if (data.analysis.suggestedReply && !replyTexts[commentId]) {
          setReplyTexts(prev => ({ ...prev, [commentId]: data.analysis.suggestedReply }));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsAnalyzing(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const fetchInbox = async (currentLimit = limit) => {
    setFetchingInbox(true);
    setWarnings({});
    try {
      const response = await fetch('/api/social/inbox', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId, limit: currentLimit, forceSync: false })
      });
      const data = await response.json();
      
      if (data.warnings) setWarnings(data.warnings);
      
      const feedPosts = (data.posts || []).filter((p: any) => p && p.id);
      
      setAllPosts(feedPosts);
      // Select first post only if we didn't have one selected or if loading fresh
      if (feedPosts.length > 0 && (!selectedPost || currentLimit === 10)) {
        setSelectedPost((prev: any) => {
          if (!prev) return feedPosts[0];
          return feedPosts.find((fp: any) => fp.id === prev.id) || prev;
        });
      }
    } catch (err) {
      console.error("Failed to fetch inbox feed", err);
    } finally {
      setFetchingInbox(false);
    }
  };

  const syncInbox = async () => {
    setFetchingInbox(true);
    setWarnings({});
    try {
      await fetch('/api/social/inbox/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId, forceSync: false })
      });
      // After sync completes, re-fetch from Firestore
      await fetchInbox(limit);
    } catch (err) {
      console.error("Failed to sync inbox", err);
      setFetchingInbox(false);
    }
  };

  useEffect(() => {
    fetchInbox(limit);
  }, [brandId, limit]);

  const loadMore = () => {
    setLimit(prev => prev + 10);
  };

  const fetchComments = async (post: any, forceRefresh = false) => {
    if (!post) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/social/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          brandId, 
          inboxItemId: post.id,
          refresh: forceRefresh
        })
      });
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to fetch comments');
        setLoading(false);
        setComments([]);
        return;
      }
      
      if (!data.success && data.platformRestriction) {
        setError(data.error);
        setLoading(false);
        setComments([]);
        return;
      }
      
      if (!data.success) {
        setError(data.error || 'Failed to fetch comments');
        setLoading(false);
        setComments([]);
        return;
      }
      
      if (data.metrics && (data.metrics.likeCount !== undefined || data.metrics.commentCount !== undefined)) {
        const fetchedCommentsCount = (data.comments || []).length;
        const updatedMetrics: any = {
           commentCount: Math.max(data.metrics.commentCount || 0, fetchedCommentsCount),
        };
        if (data.metrics.likeCount !== undefined) {
           updatedMetrics.likeCount = Math.max(data.metrics.likeCount || 0, (data.likers || []).length);
        }
        if (data.likers) {
           updatedMetrics.likers = data.likers;
        }
        
        setSelectedPost((prev: any) => {
          if (!prev || prev.id !== post.id) return prev;
          return { ...prev, ...updatedMetrics };
        });
        setAllPosts((prev: any[]) => prev.map(p => p.id === post.id ? { ...p, ...updatedMetrics } : p));
      }

      const rawComments = (data.comments || []).filter((c: any) => c && c.id);
      
      const rootsMap = new Map();
      const replies: any[] = [];
      
      rawComments.forEach((c: any) => {
        if (!c.parentId) {
          rootsMap.set(c.id, { ...c, nestedReplies: [] });
        } else {
          replies.push(c);
        }
      });
      
      replies.forEach((r: any) => {
        const parent = rootsMap.get(r.parentId);
        if (parent) {
          parent.nestedReplies.push({
            ...r,
            isReply: true,
            parentAccount: parent.authorName || parent.authorUsername || 'User'
          });
        } else {
          rootsMap.set(r.id, { ...r, nestedReplies: [] });
        }
      });
      
      const allComments = Array.from(rootsMap.values());
      
      allComments.forEach(root => {
         if (root.nestedReplies) {
            root.nestedReplies.sort((a: any, b: any) => new Date(a.created_time).getTime() - new Date(b.created_time).getTime());
         }
      });

      const sortedComments = allComments.sort((a, b) => new Date(a.created_time).getTime() - new Date(b.created_time).getTime());
      
      setComments(sortedComments);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPost && !readPostIds.includes(selectedPost.id)) {
      const nextRead = [...readPostIds, selectedPost.id];
      setReadPostIds(nextRead);
      localStorage.setItem(`readPostIds_${brandId}`, JSON.stringify(nextRead));
      onReadPost?.();
    }
    if (selectedPost) {
      fetchComments(selectedPost);
    }
  }, [selectedPost?.id, brandId]);

  const handleReply = async (commentId: string) => {
    const text = replyTexts[commentId];
    if (!text?.trim() || !selectedPost) return;

    // Find the specific comment (parent or nested) to know its source platform
    let targetComment = comments.find(c => c.id === commentId);
    if (!targetComment) {
      // Search nested replies
      for (const parent of comments) {
        const reply = parent.nestedReplies?.find((r: any) => r.id === commentId);
        if (reply) {
          targetComment = reply;
          break;
        }
      }
    }
    
    setIsReplying(prev => ({ ...prev, [commentId]: true }));
    try {
      const platform = targetComment?.platform || selectedPost.platform;
      if (!platform) throw new Error("Platform information missing.");

      const response = await fetch('/api/social/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          brandId, 
          platform, 
          commentId, 
          text,
          postId: targetComment?.nativePostId 
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send reply');
      
      // Clear input and show success
      setReplyTexts(prev => ({ ...prev, [commentId]: '' }));
      // Refresh comments to show the reply (if the API supports reading back instantly)
      fetchComments(selectedPost, true);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsReplying(prev => ({ ...prev, [commentId]: false }));
    }
  };

  return (
    <div className="flex bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden h-[600px]">
      {/* Post Sidebar */}
      <div className="w-1/3 border-r border-gray-100 overflow-y-auto">
        <div className="p-4 border-b border-gray-50 flex items-center justify-between sticky top-0 bg-white/80 backdrop-blur-md z-10">
          <h3 className="text-xs font-black uppercase tracking-widest text-gray-400">Social Inbox</h3>
          <button 
            onClick={() => syncInbox()}
            disabled={fetchingInbox}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            <RefreshCw className={`w-3 h-3 text-gray-400 ${fetchingInbox ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {Object.keys(warnings).length > 0 && (
          <div className="bg-amber-50 p-3 text-[10px] text-amber-800 font-medium border-b border-amber-100 divide-y divide-amber-100/30 line-clamp-3">
            {Object.entries(warnings).map(([key, msg]) => (
               <div key={key} className="py-1 first:pt-0 last:pb-0 flex items-start gap-1.5 opacity-90">
                 <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                 <span>{msg}</span>
               </div>
            ))}
          </div>
        )}
        
        <div className="divide-y divide-gray-50">
          {fetchingInbox && allPosts.length === 0 && (
            <div className="p-8 text-center animate-pulse">
               <RefreshCw className="w-6 h-6 text-indigo-200 animate-spin mx-auto mb-2" />
               <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Scanning Networks...</p>
            </div>
          )}
          {allPosts.map((post) => {
            const isUnread = !readPostIds.includes(post.id);
            return (
              <button
                key={`${post.platform}-${post.id}`}
                onClick={() => setSelectedPost(post)}
                className={`w-full p-4 flex gap-3 text-left transition-all hover:bg-gray-50 relative ${
                  selectedPost?.id === post.id ? 'bg-indigo-50/50 border-r-4 border-indigo-600' : ''
                }`}
              >
                {isUnread && (
                  <div className="absolute top-4 right-4 w-2 h-2 bg-indigo-600 rounded-full animate-pulse shadow-[0_0_8px_rgba(79,70,229,0.5)]" />
                )}
                <div className="w-12 h-12 rounded-2xl bg-gray-100 overflow-hidden shrink-0 border border-gray-200 flex items-center justify-center">
                {post.postThumbnailUrl ? (
                  <img src={post.postThumbnailUrl} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  post.type === 'direct_message_thread' ? (
                    <Send className="w-5 h-5 text-indigo-500" />
                  ) : (
                    <MessageCircle className="w-5 h-5 text-gray-300" />
                  )
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 truncate leading-tight mb-1">{(post.latestMessageOrComment?.text || post.postText || '').slice(0, 45)}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{post.platform}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(post.likeCount > 0 || post.platform?.toLowerCase() === 'threads') && (
                      <div className="flex items-center gap-0.5 text-[10px] font-black text-pink-500">
                        <Heart className="w-2.5 h-2.5 fill-current" />
                        {post.likeCount ?? '—'}
                      </div>
                    )}
                    {(post.commentCount > 0 || post.platform?.toLowerCase() === 'threads') && (
                      <div className="flex items-center gap-0.5 text-[10px] font-black text-indigo-500">
                        <MessageCircle className="w-2.5 h-2.5" />
                        {post.commentCount ?? '—'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </button>
          )})}

          {allPosts.length > 0 && allPosts.length >= limit && (
            <button 
              onClick={() => loadMore()}
              disabled={fetchingInbox}
              className="w-full p-4 text-xs font-bold text-indigo-600 hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors"
            >
              {fetchingInbox ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Load More'}
            </button>
          )}
          
          {allPosts.length === 0 && !fetchingInbox && (
            <div className="p-12 text-center">
              <p className="text-sm text-gray-400 font-medium italic">No activity found recently.</p>
              <button 
                onClick={() => syncInbox()}
                className="mt-4 text-xs font-bold text-indigo-600 hover:underline"
              >
                Scan for recent posts
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messaging Area */}
      <div className="flex-1 flex flex-col bg-gray-50/30">
        {selectedPost ? (
          <>
            <div className="p-4 border-b border-gray-100 bg-white flex items-center justify-between">
              <div className="flex items-center gap-6">
                <button 
                  onClick={() => setActiveDetailTab('comments')}
                  className={`flex items-center gap-2 pb-4 -mb-4 transition-all border-b-2 ${activeDetailTab === 'comments' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-xs font-black uppercase tracking-widest">{selectedPost.type === 'direct_message_thread' ? 'Messages' : 'Comments'} ({selectedPost.commentCount || comments.length}) <span className="text-[7px] text-gray-400 font-mono font-normal lowercase tracking-normal ml-2 opacity-60">ID: #{selectedPost.replyTarget?.id?.slice(-6) || selectedPost.id?.slice(-6) || 'none'}</span></span>
                </button>
                <button 
                  onClick={() => setActiveDetailTab('likes')}
                  className={`flex items-center gap-2 pb-4 -mb-4 transition-all border-b-2 ${activeDetailTab === 'likes' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                >
                  <Heart className={`w-4 h-4 ${activeDetailTab === 'likes' ? 'fill-current' : ''}`} />
                  <span className="text-xs font-black uppercase tracking-widest">Likes ({selectedPost.likeCount ?? '—'})</span>
                </button>

                {selectedPost.platform?.toLowerCase() === 'threads' && (
                  <div className="flex items-center gap-4 opacity-60">
                    <div className="flex items-center gap-1">
                      <RefreshCw className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-[10px] font-black text-orange-600 uppercase tracking-widest">{selectedPost.repostCount ?? '—'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BarChart3 className="w-3.5 h-3.5 text-cyan-500" />
                      <span className="text-[10px] font-black text-cyan-600 uppercase tracking-widest">{selectedPost.quoteCount ?? '—'}</span>
                    </div>
                  </div>
                )}
              </div>
              <button 
                onClick={() => fetchComments(selectedPost, true)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeDetailTab === 'comments' ? (
                <div className="space-y-6">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-3">
                      <RefreshCw className="w-8 h-8 text-indigo-200 animate-spin" />
                      <p className="text-xs font-black text-gray-300 uppercase tracking-widest">Loading Conversation...</p>
                    </div>
                  ) : error ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-3 p-8 text-center bg-red-50/50 rounded-3xl border border-dashed border-red-100">
                        <AlertCircle className="w-8 h-8 text-red-300" />
                        <p className="text-sm font-bold text-red-900">Platform Restriction</p>
                        <p className="text-xs text-red-600 max-w-[280px] leading-relaxed">{error}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <button 
                            onClick={() => window.location.reload()} 
                            className="text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:underline"
                          >
                            Try Refreshing
                          </button>
                          <span className="text-gray-300">|</span>
                          <button 
                            onClick={() => {
                              alert("If you've followed the steps and still see this, check these Meta Developer Portal settings:\n\n1. Ensure your Meta App is in 'Live' Mode (not Development).\n2. If in Development, ensure your Threads account email is added as a 'Tester' in 'App Roles'.\n3. Verify your App has 'Advanced Access' specifically for 'threads_manage_replies'.");
                            }} 
                            className="text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-indigo-600 hover:underline"
                          >
                            Step-by-step fix
                          </button>
                        </div>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-3 text-center grayscale opacity-40">
                      <Quote className="w-12 h-12 text-gray-300" />
                      {selectedPost.commentCount > 0 ? (
                        <>
                          <p className="text-sm font-bold text-gray-400">Comments exist on this item, but they could not be loaded.</p>
                          <p className="text-xs text-gray-400 max-w-[250px]">Check platform permissions or reconnect your account.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-bold text-gray-400">No comments yet</p>
                          <p className="text-xs text-gray-400 max-w-[200px]">When users interact with your post, they'll show up here.</p>
                        </>
                      )}
                    </div>
                  ) : (
                    comments && comments.filter(Boolean).map((comment, cidx) => (
                      <div key={`parent-${comment?.id || cidx}-${cidx}`} className="space-y-4">
                        {/* Parent Comment */}
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex gap-4"
                        >
                          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 border shadow-sm overflow-hidden bg-indigo-100 border-indigo-200">
                            {comment?.profile_picture_url ? (
                              <img src={comment.profile_picture_url} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              comment?.isMessage ? (
                                <Send className="w-5 h-5 text-indigo-600" />
                              ) : (
                                <User className="w-5 h-5 text-indigo-600" />
                              )
                            )}
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm relative group">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs font-black text-gray-900 uppercase tracking-tight">@{comment?.authorUsername || comment?.authorName || comment?.username || (comment?.from?.name || 'User')}</span>
                                    {comment?.is_verified && (
                                      <CheckCircle2 className="w-3 h-3 text-blue-500 fill-blue-50" />
                                    )}
                                  </div>
                                  {comment?.platform && (
                                    <span className="text-[8px] font-black px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full uppercase tracking-widest border border-gray-200">
                                      {comment.platform}
                                    </span>
                                  )}
                                  <span className="text-[7px] text-gray-300 font-mono">#{comment?.id?.slice(-6) || '???'} {comment?.reply_to?.id ? `-> #${comment.reply_to.id.slice(-6)}` : '(no parent)'}</span>
                                </div>
                                <span className="text-[10px] text-gray-400">{safeDateFormat(comment?.timestamp || comment?.created_time)}</span>
                              </div>
                              <p className="text-sm text-gray-600 font-medium leading-relaxed">{comment?.text || comment?.message}</p>
                              
                              {comment?.id && sentimentMap[comment.id] && (
                                <div className="mt-3 p-3 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 rounded-xl border border-indigo-100/50">
                                  <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-indigo-700">
                                    <div className="flex items-center gap-1">
                                      <Sparkles className="w-3 h-3 text-indigo-500" />
                                      Sentiment: <span className={
                                        sentimentMap[comment.id].sentiment === 'positive' ? 'text-emerald-500' :
                                        sentimentMap[comment.id].sentiment === 'negative' ? 'text-red-500' : 'text-blue-500'
                                      }>{sentimentMap[comment.id].sentiment}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Zap className="w-3 h-3 text-indigo-500" />
                                      Urgency: <span className={
                                        sentimentMap[comment.id].urgency === 'high' ? 'text-red-500' :
                                        sentimentMap[comment.id].urgency === 'medium' ? 'text-orange-500' : 'text-gray-500'
                                      }>{sentimentMap[comment.id].urgency}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Activity className="w-3 h-3 text-indigo-500" />
                                      Intent: <span>{sentimentMap[comment.id].intent}</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {comment?.id && !sentimentMap[comment.id] && (
                                <button
                                  onClick={() => analyzeSentiment(comment.id, comment.text || comment.message)}
                                  disabled={isAnalyzing[comment.id]}
                                  className="mt-2 p-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg text-[10px] flex items-center gap-1 font-bold transition-colors"
                                >
                                  {isAnalyzing[comment.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                  {isAnalyzing[comment.id] ? 'Analyzing...' : 'Analyze Sentiment'}
                                </button>
                              )}

                              {comment?.like_count > 0 && (
                                <div className="absolute -bottom-2 -right-2 bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full text-[10px] font-bold border border-pink-100 flex items-center gap-1">
                                  <Heart className="w-3 h-3 fill-current" />
                                  {comment.like_count}
                                </div>
                              )}
                            </div>

                            {comment?.canReply === true && (
                              <div className="flex items-center gap-2 pl-4 mt-2">
                                <input
                                  type="text"
                                  value={comment?.id ? (replyTexts[comment.id] || '') : ''}
                                  onChange={(e) => comment?.id && setReplyTexts(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                  placeholder="Type a response..."
                                  className="flex-1 bg-white border border-gray-200 px-4 py-2 rounded-xl text-xs font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                />
                                <button
                                  onClick={() => comment?.id && handleReply(comment.id)}
                                  disabled={!comment?.id || isReplying[comment.id] || !replyTexts[comment.id]?.trim()}
                                  className="bg-indigo-600 text-white p-2 rounded-xl hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50"
                                >
                                  {comment?.id && isReplying[comment.id] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>

                        {/* Nested Replies */}
                        <div className="relative">
                          {comment?.nestedReplies && comment.nestedReplies.length > 0 && (
                            <div className="absolute left-[19px] top-0 bottom-8 w-px bg-gray-100" />
                          )}
                          <div className="space-y-6 mt-4">
                            {comment?.nestedReplies && comment.nestedReplies.filter(Boolean).map((reply: any, ridx: number) => (
                              <motion.div 
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                key={`reply-${reply?.id || ridx}-${ridx}`} 
                                className="flex gap-4 ml-12 relative"
                              >
                                {/* Horizontal connector */}
                                <div className="absolute -left-7 top-4 w-7 h-px bg-gray-100" />
                                
                                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 border shadow-sm overflow-hidden bg-gray-50 border-gray-100 z-10">
                                  {reply?.profile_picture_url ? (
                                    <img src={reply.profile_picture_url} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                  ) : (
                                    <User className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1 space-y-3">
                                  <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm relative group">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1">
                                          <span className="text-xs font-black text-gray-900 uppercase tracking-tight">@{reply?.authorUsername || reply?.authorName || reply?.username || (reply?.from?.name || 'User')}</span>
                                          {reply?.is_verified && (
                                            <CheckCircle2 className="w-3 h-3 text-blue-500 fill-blue-50" />
                                          )}
                                        </div>
                                        <span className="text-[10px] text-gray-400 font-medium">
                                          replied to <span className="text-indigo-600 font-bold">@{reply?.parentAccount || 'User'}</span>
                                        </span>
                                        {reply?.platform && (
                                          <span className="text-[8px] font-black px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full uppercase tracking-widest border border-gray-200">
                                            {reply.platform}
                                          </span>
                                        )}
                                        {/* DIAGNOSTIC ID */}
                                        <span className="text-[7px] text-gray-300 font-mono">#{reply?.id?.slice(-6) || '???'}</span>
                                      </div>
                                      <span className="text-[10px] text-gray-400">{safeDateFormat(reply?.timestamp || reply?.created_time)}</span>
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium leading-relaxed">{reply?.text || reply?.message}</p>
                                    
                                    {reply?.id && sentimentMap[reply.id] && (
                                      <div className="mt-3 p-3 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 rounded-xl border border-indigo-100/50">
                                        <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-indigo-700">
                                          <div className="flex items-center gap-1">
                                            <Sparkles className="w-3 h-3 text-indigo-500" />
                                            Sentiment: <span className={
                                              sentimentMap[reply.id].sentiment === 'positive' ? 'text-emerald-500' :
                                              sentimentMap[reply.id].sentiment === 'negative' ? 'text-red-500' : 'text-blue-500'
                                            }>{sentimentMap[reply.id].sentiment}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Zap className="w-3 h-3 text-indigo-500" />
                                            Urgency: <span className={
                                              sentimentMap[reply.id].urgency === 'high' ? 'text-red-500' :
                                              sentimentMap[reply.id].urgency === 'medium' ? 'text-orange-500' : 'text-gray-500'
                                            }>{sentimentMap[reply.id].urgency}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Activity className="w-3 h-3 text-indigo-500" />
                                            Intent: <span>{sentimentMap[reply.id].intent}</span>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {reply?.id && !sentimentMap[reply.id] && (
                                      <button
                                        onClick={() => analyzeSentiment(reply.id, reply.text || reply.message)}
                                        disabled={isAnalyzing[reply.id]}
                                        className="mt-2 p-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg text-[10px] flex items-center gap-1 font-bold transition-colors"
                                      >
                                        {isAnalyzing[reply.id] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                        {isAnalyzing[reply.id] ? 'Analyzing...' : 'Analyze Sentiment'}
                                      </button>
                                    )}
                                  </div>

                                  {reply?.canReply === true && (
                                    <div className="flex items-center gap-2 pl-4 mt-2">
                                      <input
                                        type="text"
                                        value={reply?.id ? (replyTexts[reply.id] || '') : ''}
                                        onChange={(e) => reply?.id && setReplyTexts(prev => ({ ...prev, [reply.id]: e.target.value }))}
                                        placeholder="Type a response..."
                                        className="flex-1 bg-white border border-gray-200 px-4 py-2 rounded-xl text-xs font-medium focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
                                      />
                                      <button
                                        onClick={() => reply?.id && handleReply(reply.id)}
                                        disabled={!reply?.id || isReplying[reply.id] || !replyTexts[reply.id]?.trim()}
                                        className="bg-indigo-600 text-white p-2 rounded-xl hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50"
                                      >
                                        {reply?.id && isReplying[reply.id] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm text-center">
                    <div className="w-16 h-16 rounded-full bg-pink-50 flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-pink-500 fill-current" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 mb-1">{selectedPost.likeCount || 0} Likes</h3>
                    <p className="text-sm font-medium text-gray-400">Activity summary for this post</p>
                  </div>

                  {selectedPost.likers && selectedPost.likers.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-black text-gray-400 uppercase tracking-widest pl-2">Recent Likers</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedPost.likers.map((liker: any, lidx: number) => (
                          <div key={`liker-${liker.id || lidx}`} className="bg-white p-3 rounded-2xl border border-gray-100 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-400" />
                            </div>
                            <span className="text-sm font-bold text-gray-700">{liker.name || 'Anonymous User'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(selectedPost.platform?.toLowerCase() === 'instagram' || selectedPost.platform?.toLowerCase() === 'threads') && (
                    <div className="bg-indigo-50/50 p-6 rounded-3xl border border-dashed border-indigo-100 flex items-center gap-4">
                      <div className="p-3 rounded-2xl bg-white shadow-sm">
                        <AlertCircle className="w-5 h-5 text-indigo-400" />
                      </div>
                      <p className="text-xs font-medium text-indigo-700 leading-relaxed">
                        Due to {selectedPost.platform?.toLowerCase() === 'instagram' ? 'Instagram' : 'Threads'} privacy restrictions, we can only display the total count. Detailed lists of likers are restricted to individual user profiles.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-12">
            <div className="w-16 h-16 rounded-3xl bg-gray-100 flex items-center justify-center mb-6">
              <MessageCircle className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2">Select a post to start chatting</h3>
            <p className="text-sm text-gray-400 font-medium max-w-[240px]">Select any published post from the left to manage user comments and replies.</p>
          </div>
        )}
      </div>
    </div>
  );
};
